import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { CodeGlyph } from '@/components/brand/logo-mark'
import { Container } from '@/components/generals/container'
import { navLinks } from '@/constants/portfolio'
import { siteConfig } from '@/constants/site'

const SOCIAL_LABELS: Record<string, string> = {
    github: 'GitHub',
    linkedin: 'LinkedIn',
    dribbble: 'Dribbble',
    instagram: 'Instagram'
}

const sitemap = [
    { label: "Home", href: '/' },
    { label: "All work", href: '/work' },
    ...navLinks.filter(link => link.href !== '/#work')
]

export const Footer: FC = (): ReactNode => {
    const socials = Object.entries(siteConfig.links ?? {}).filter(([, url]) => Boolean(url))

    const scrollToTop = () => {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' })
    }

    return (
        <footer className='relative overflow-hidden border-t border-border pt-20 md:pt-28'>
            <Container>
                <div className='grid gap-14 md:grid-cols-12'>
                    <div className='md:col-span-6'>
                        <p className='font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground'>
                            Have a project in mind?
                        </p>

                        <Link
                            href='/#contact'
                            className='group mt-5 inline-flex items-center gap-4 text-4xl font-semibold tracking-tight md:gap-6 md:text-6xl'
                        >
                            Let's work together

                            <span
                                aria-hidden='true'
                                className='grid size-12 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground transition-transform duration-500 group-hover:rotate-45 md:size-16'
                            >
                                <ArrowUpRight className='size-5 md:size-6' />
                            </span>
                        </Link>
                    </div>

                    <nav aria-label="Footer" className='md:col-span-3'>
                        <p className='font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground'>
                            Sitemap
                        </p>

                        <ul className='mt-5 space-y-3'>
                            {sitemap.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className='link-underline text-foreground/90 hover:text-foreground'>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='md:col-span-3'>
                        <p className='font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground'>
                            {socials.length > 0 ? "Elsewhere" : "Status"}
                        </p>

                        {socials.length > 0 ? (
                            <ul className='mt-5 space-y-3'>
                                {socials.map(([key, url]) => (
                                    <li key={key}>
                                        <a
                                            href={url}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='link-underline inline-flex items-center gap-1 text-foreground/90 hover:text-foreground'
                                        >
                                            {SOCIAL_LABELS[key] ?? key}
                                            <ArrowUpRight aria-hidden='true' className='size-4' />
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p className='mt-5 flex items-center gap-2 text-foreground/90'>
                                <span aria-hidden='true' className='pulse-dot size-2 rounded-full bg-emerald-400' />
                                {siteConfig.availability}
                            </p>
                        )}

                        <button
                            type='button'
                            onClick={scrollToTop}
                            className='group mt-8 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground'
                        >
                            Back to top

                            <ArrowUp aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5' />
                        </button>
                    </div>
                </div>
            </Container>

            <div aria-hidden='true' className='mt-16 select-none md:mt-24'>
                <Container>
                    <p className='bg-gradient-to-b from-white/85 via-white/30 to-white/0 bg-clip-text text-center text-[23vw] font-bold leading-[0.8] tracking-[-0.06em] text-transparent xl:text-[20.5rem]'>
                        {siteConfig.shortName}
                    </p>
                </Container>
            </div>

            <Container className='flex flex-col gap-3 border-t border-border py-6 text-xs text-muted-foreground md:flex-row md:items-center md:justify-between'>
                <p>
                    © {siteConfig.year} {siteConfig.name}. All rights reserved.
                </p>

                <p className='flex items-center gap-2'>
                    Designed and built with
                    <CodeGlyph className='w-4 text-brand-bright' />
                    by {siteConfig.name.split(' ')[0]}
                </p>
            </Container>
        </footer>
    )
}
